import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { usePresence } from '../hooks/usePresence';
import styles from './Popover.module.css';

export interface PopoverProps {
  /** Renders the trigger; spread the props onto a `Button`. */
  trigger: (props: {
    'aria-expanded': boolean;
    'aria-controls': string;
    onClick: () => void;
    ref: (element: HTMLElement | null) => void;
  }) => ReactNode;
  /** Accessible name of the panel, e.g. "Filter". */
  label: string;
  align?: 'start' | 'end';
  children: ReactNode;
}

/**
 * Non-modal panel anchored to a button, e.g. for list filters. Changes apply
 * immediately; Escape or a click outside closes it and focus returns to the
 * trigger. On phones it docks to the bottom of the screen.
 */
export function Popover({ trigger, label, align = 'start', children }: PopoverProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLElement | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { present, state } = usePresence(open, panelRef);

  useEffect(() => {
    if (!open) return;
    panelRef.current?.focus();
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!panelRef.current?.contains(target) && !triggerRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={styles.root}>
      {trigger({
        'aria-expanded': open,
        'aria-controls': id,
        onClick: () => setOpen((o) => !o),
        ref: (element) => {
          triggerRef.current = element;
        },
      })}
      <div
        ref={panelRef}
        id={id}
        role="dialog"
        aria-label={label}
        tabIndex={-1}
        hidden={!present}
        data-state={state}
        className={`${styles.panel} ${styles[align]}`}
      >
        {children}
      </div>
    </div>
  );
}
